import en from '../translations/en.json';
import nl from '../translations/nl.json';
import de from '../translations/de.json';
import es from '../translations/es.json';
import fr from '../translations/fr.json';
import it from '../translations/it.json';
import cs from '../translations/cs.json';
import pl from '../translations/pl.json';
import sk from '../translations/sk.json';

export type Locale = 'en' | 'nl' | 'de' | 'es' | 'fr' | 'it' | 'cs' | 'pl' | 'sk';

const translations: Record<Locale, typeof en> = {
  en,
  nl,
  de,
  es,
  fr,
  it,
  cs,
  pl,
  sk,
};

type TranslationKey =
  | keyof typeof en.Calendar
  | `errors.${keyof typeof en.Calendar.errors}`
  | keyof typeof en.HotelOverview;

export function createTranslator(locale: Locale = 'en') {
  const messages = translations[locale] || translations.en;

  return function t(key: TranslationKey, values?: Record<string, string | number>): string {
    // Handle nested keys like "errors.pastDate" or "Calendar.arrival" or "HotelOverview.viewDetails"
    const parts = key.split('.');

    // Determine the top-level namespace (Calendar or HotelOverview)
    let value: any;

    if (key.includes('errors.')) {
      // Handle Calendar error keys
      value = messages.Calendar;
      for (const k of parts) {
        value = value?.[k];
        if (value === undefined) {
          console.warn(`Missing translation for key: Calendar.${key}`);
          return key;
        }
      }
    } else if (key in (messages.HotelOverview || {})) {
      // Handle HotelOverview keys
      value = messages.HotelOverview?.[key as keyof typeof messages.HotelOverview];
      if (value === undefined) {
        console.warn(`Missing translation for key: HotelOverview.${key}`);
        return key;
      }
    } else {
      // Handle Calendar keys
      value = messages.Calendar?.[key as keyof typeof messages.Calendar];
      if (value === undefined) {
        console.warn(`Missing translation for key: Calendar.${key}`);
        return key;
      }
    }

    if (typeof value !== 'string') {
      console.warn(`Translation for key ${key} is not a string:`, value);
      return key;
    }

    // Replace placeholders like {nights} with actual values
    if (values) {
      return value.replace(/\{(\w+)\}/g, (match, placeholder) => {
        return String(values[placeholder] ?? match);
      });
    }

    return value;
  };
}

export function createFormatter(locale: Locale = 'en') {
  return {
    dateTime: (date: Date, options?: Intl.DateTimeFormatOptions) => {
      return new Intl.DateTimeFormat(locale, options).format(date);
    },
    number: (value: number, options?: Intl.NumberFormatOptions) => {
      return new Intl.NumberFormat(locale, options).format(value);
    },
  };
}
